import { Injectable } from "@angular/core";
import { ItemDataService } from "../item-data.service";
import { GearTierType, TieredGear, FlatItemData, ItemData } from "../../models/item-data-models";
import { trackError } from "../../app.config";
import { Filter } from "../../models/filter-models";

@Injectable({
  providedIn: 'root'
})
export class GearTierService {
  private allItemData: ItemData[];
  private flatItemData: FlatItemData[];
  private gearTierData: Record<string, GearTierType>;

  private defaultTieredGear!: TieredGear[];

  private tieredGear: TieredGear[] = [];

  constructor(private itemDataService: ItemDataService) {
    this.allItemData = this.itemDataService.getItemData();
    this.flatItemData = this.itemDataService.getFlatItemData();
    this.gearTierData = {};
    const structuredGearTierData = this.itemDataService.getGearTierListData();
    structuredGearTierData.forEach((tier) => {
      tier.baseTypes.forEach(bt => {
        this.gearTierData[bt] = tier.tier;
      })
    });

    this.buildDefaultGearTierListData();
  }

  buildDefaultGearTierListData() {
    // Iterate over all gear item types. Assume that the first time we encounter an image, it's Basic, then Advanced, then Expert.

    const basicItems: string[] = [];
    const advancedItems: string[] = [];
    const expertItems: string[] = [];
    const eliteItems: string[] = [];
    const masterItems: string[] = [];

    // Not sure if we still need this intermediate step
    const gearTierListData: { tier: GearTierType, items: string[] }[] = [
      { tier: 'Basic', items: basicItems },
      { tier: 'Advanced', items: advancedItems },
      { tier: 'Expert', items: expertItems },
      { tier: 'Elite', items: eliteItems },
      { tier: 'Master', items: masterItems },
    ];

    this.allItemData.filter(i => i.isGear).forEach(itemType => {
      for (const item of itemType.baseTypes) {
        const tier = this.gearTierData[item.name];
        if (tier === 'Master') {
          masterItems.push(item.name);
        } else if (tier === 'Elite') {
          eliteItems.push(item.name);
        } else if (tier === 'Expert') {
          expertItems.push(item.name);
        } else if (tier === 'Advanced') {
          advancedItems.push(item.name);
        } else {
          basicItems.push(item.name);
        }
      }
    });

    const data: TieredGear[] = [];

    const flatGearDefenceType = this.itemDataService.getGearDefenceType();

    for (const gearTier of gearTierListData) {
      const tier = gearTier.tier;
      const items = gearTier.items;

      data.push(
        ...items.map(item => {
          const defenceType = flatGearDefenceType.find(i => i.name === item)?.defenceType;

          return {
            name: item,
            itemClass: this.itemDataService.getFlatItemDataByBaseTypeName(item)?.itemClass || 'Other',
            tier,
            defenceType: defenceType
          }
        })
      )
    }

    this.defaultTieredGear = data;
  }

  combineGearTierListData(filter: Filter) {
    const gearData = this.itemDataService.getGearData()();

    const clone = structuredClone(this.defaultTieredGear) as TieredGear[];

    clone.forEach(item => {
      const gear = gearData.find(g => g.name === item.name);
      item.dropLvl = gear?.dropLvl || gear?.lvl; // Drop level on gear is same as level requirement
    });

    filter.customGearTiers.forEach(customItem => {
      const existingItem = clone.find(item => item.name === customItem.baseType);
      if (existingItem) {
        existingItem.originalTier = existingItem.tier;
        existingItem.tier = customItem.tier;
      } else {
        trackError(new Error('Unexpected custom gear: ' + customItem.baseType));
      }
    });

    this.tieredGear.length = 0;
    this.tieredGear.push(...clone);
  }

  getGearTierData() {
    return this.tieredGear;
  }
}
